import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@f2e/ui/components/base"
import { ellipsis } from "@f2e/shared/utils"

import { UserAvatar, PageTable } from "@/components"
import { PermissionGuard } from "@/components/Auth"
import { RoleSelect } from "@/components/User"
import { useUpdateOrganizationUserRoleApi } from "@/hooks/api"
import {
  Member,
  Organization,
  OrganizationRole,
  PageInfo,
  Permission,
  Scope,
} from "@/types"
import { useStore } from "@/store"
import { isOwner } from "@/lib/utils"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

import { ActionButton } from "./ActionButton"

export interface OrganizationUserTableProps {
  organization: Organization
  members: Member[]
  pageInfo: PageInfo
  onPageChange?: (page: number) => void
  onRoleChange?: (member: Member, role: OrganizationRole) => void
  onRemove?: (member: Member) => void
  onError?: (error: Error) => void
}

const OrganizationUserTable = ({
  organization,
  members,
  pageInfo,
  onPageChange,
  onRoleChange,
  onRemove,
  onError,
}: OrganizationUserTableProps) => {
  const { t } = useTranslation()
  const [userMe] = useStore((state) => [state.userMe])

  return (
    <PageTable<Member>
      className="mt-4"
      columns={[
        {
          title: `${t("settings.users.title")} (${pageInfo.totalRecords})`,
          width: "65%",
          render: (member) => (
            <div className="flex">
              <div className="flex items-center">
                <UserAvatar user={member} className="h-10 w-10" />
                <div className="grid grid-rows-2 ml-4">
                  <div className="text-base">
                    <span>{ellipsis(member.name, 30)}</span>
                    <span>
                      {userMe?.id === member.id && (
                        <Badge variant="secondary" className="ml-2 py-0">
                          {t("common.you")}
                        </Badge>
                      )}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500">{member.email}</div>
                </div>
              </div>
            </div>
          ),
        },
        {
          title: t("settings.users.role"),
          width: "20%",
          render: (member) => (
            <PermissionGuard permission={Permission.OrgUpdateRole} disabled>
              <UserRoleSelect
                user={member}
                organization={organization}
                disabled={isOwner(member.role)}
                onChange={(role) => onRoleChange?.(member, role)}
                onError={onError}
              />
            </PermissionGuard>
          ),
        },
        {
          title: t("settings.users.actions"),
          width: "15%",
          render: (member) => (
            <ActionButton member={member} onRemove={onRemove} />
          ),
        },
      ]}
      data={members}
      defaultPage={pageInfo.current}
      totalPages={pageInfo.totalPages}
      onPageChange={onPageChange}
    />
  )
}

const UserRoleSelect = ({
  user,
  organization,
  disabled = false,
  onChange,
  onError,
}: {
  user: Member
  organization: Organization
  disabled?: boolean
  onChange?: (role: OrganizationRole) => void
  onError?: (error: Error) => void
}) => {
  const { t } = useTranslation()
  const {
    error: updateUserRoleError,
    data: updateUserRoleResp,
    fetchFn: updateUserRole,
  } = useUpdateOrganizationUserRoleApi(
    organization.id.toString(),
    user.id.toString()
  )

  useEffect(() => {
    if (!updateUserRoleError) return
    onError?.(
      new Error(
        t("settings.users.organizationUserTable.roleChangeFailed.description", {
          message: getUserFacingApiErrorMessage(
            updateUserRoleError,
            t,
            updateUserRoleResp?.message || ""
          ),
        })
      )
    )
  }, [onError, updateUserRoleError, updateUserRoleResp, t])

  const handleRoleChange = (role: OrganizationRole) => {
    updateUserRole({
      jsonBody: {
        user_id: user.id,
        role_name: role,
      },
      callbackFn: (resp) => {
        if (!resp || !resp.data) return
        onChange?.(role)
      },
    })
  }

  return (
    <RoleSelect
      value={user.role}
      mode={Scope.Organization}
      readOnly={disabled}
      onChange={(role) => handleRoleChange(role as OrganizationRole)}
    />
  )
}

export default OrganizationUserTable
